import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Modal } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import dayjs from 'dayjs';

import { useUpdateRecords } from '../features/records';
import Help from '../images/help.svg?react';
import { temperatureContext } from '../lib/context';
import { convertTemperature, convertToCelsius, convertToSecond, secondToTime } from '../lib/convert';
import WorkingTimeRangesEditor from './WorkingTimeRangesEditor';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '420px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'transparent',
    boxShadow: 'none',
    borderRadius: '32px',
    p: 0,
};

const LightTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
    ({ theme }: any) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.common.white,
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: theme.shadows[3],
            fontSize: 10,
            padding: '10px 5px',
        },
    })
);

const textInputClasses =
    'h-11 rounded-full border border-[#E1E5ED] bg-white px-4 text-sm font-medium text-[#1F2A37] transition focus:border-[#3F8EE8] focus:outline-none';

const pillButton = (active: any) =>
    `rounded-full px-4 py-2 text-sm font-semibold transition ${
        active ? 'bg-[#3F8EE8] text-white shadow-[0px_10px_20px_rgba(63,142,232,0.35)]' : 'text-[#7B87A1]'
    }`;

const chipButton = (active: any) =>
    `rounded-full border px-3 py-1 text-xs font-semibold ${
        active ? 'border-[#3F8EE8] bg-[#E2F0FF] text-[#1C65D8]' : 'border-[#E4E8F3] text-[#7B87A1]'
    }`;

function Edit(props: any) {
    const {
        openConfig,
        setOpenConfig,
        setSaveAlert,
        setAlertState,
        selectedDeviceId,
        selectedIndex,
        selectedRecords = [],
        tKey,
        ro,
    } = props;
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const { t: tNotify } = useTranslation(undefined, { keyPrefix: 'Notification' });
    const updateRecords = useUpdateRecords();

    const tempUnit = useContext(temperatureContext);

    const [configType, setConfigType] = React.useState('info');
    const [rangeType, setRangeType] = React.useState('continuous');
    const [upperUnit, setUpperUnit] = React.useState(tempUnit);
    const [lowerUnit, setLowerUnit] = React.useState(tempUnit);
    const [timeUnit, setTimeUnit] = React.useState('minute');
    const [deviceListOpen, setDeviceListOpen] = React.useState(false);
    const [intervalUnit, setIntervalUnit] = React.useState('minute');
    const [activeRecordId, setActiveRecordId] = React.useState(null);
    const [loggingInterval, setLoggingInterval] = React.useState(10);
    const [alertRanges, setAlertRanges] = React.useState([
        { start: dayjs().startOf('day'), end: dayjs().endOf('day') },
    ]);

    const shipmentNumberInput = React.useRef(null);
    const shipperInput = React.useRef(null);
    const startLocationInput = React.useRef(null);
    const carrierInput = React.useRef(null);
    const consigneeInput = React.useRef(null);
    const endLocationInput = React.useRef(null);
    const noteInput = React.useRef(null);

    const upLimitInput = React.useRef(null);
    const lowLimitInput = React.useRef(null);
    const timeLimitInput = React.useRef(null);
    const intervalInput = React.useRef(null);

    const emailInput1 = React.useRef(null);
    const emailInput2 = React.useRef(null);
    const emailInput3 = React.useRef(null);

    const now = dayjs();
    const timeOffset = now.utcOffset();
    const timeZoneOffset = new Date().getTimezoneOffset();
    const offsetHour = timeZoneOffset / 60;
    const offsetSign = offsetHour > 0 ? '-' : '+';
    const offsetString = `GMT${offsetSign}${Math.abs(offsetHour).toString()}`;

    useEffect(() => {
        setUpperUnit(tempUnit);
        setLowerUnit(tempUnit);
    }, [tempUnit]);

    const handleWorkingTimeChange = React.useCallback((ranges: any) => {
        setAlertRanges(ranges);
    }, []);

    const handleSwitch = (event: any) => {
        setConfigType(event.currentTarget.id);
    };

    const getRefValue = (ref: any, fallback: any = '') => ref?.current?.value ?? fallback;

    const parseOrFallback = (value: any, parser: any, fallback: any = 0) => {
        const parsed = parser(value);
        return Number.isNaN(parsed) ? fallback : parsed;
    };

    const handleSaveAll = () => {
        const newConfig = {
            shipment_number: getRefValue(shipmentNumberInput),
            shipper_name: getRefValue(shipperInput),
            carrier_name: getRefValue(carrierInput),
            consignee_name: getRefValue(consigneeInput),
            start_location: getRefValue(startLocationInput),
            end_location: getRefValue(endLocationInput),
            note: getRefValue(noteInput),
            notify_emails: [emailInput1, emailInput2, emailInput3].map((ref: any) => getRefValue(ref)),
            alert_time: alertRanges
                .filter((r: any) => r.start && r.end)
                .map((r: any) => ({
                    start_time: dayjs(r.start).subtract(timeOffset, 'm').format('YYYY-MM-DD HH:mm:ss'),
                    end_time: dayjs(r.end).subtract(timeOffset, 'm').format('YYYY-MM-DD HH:mm:ss'),
                })),
            upper_limit: convertToCelsius(parseOrFallback(getRefValue(upLimitInput), parseFloat, 0), upperUnit),
            lower_limit: convertToCelsius(parseOrFallback(getRefValue(lowLimitInput), parseFloat, 0), lowerUnit),
            time_limit: convertToSecond(
                parseOrFallback(getRefValue(timeLimitInput), (v: any) => parseInt(v, 10), 0),
                timeUnit
            ),
            type: rangeType,
        };

        // The record lists refresh themselves once the save lands (see useUpdateRecords).
        updateRecords.mutate(
            { deviceIds: selectedDeviceId, indexes: selectedIndex, record: newConfig },
            {
                onSuccess: () => {
                    setSaveAlert(true);
                    setAlertState({ severity: 'success', message: t('saveSuccess') });
                },
                onError: () => {
                    setSaveAlert(true);
                    setAlertState({ severity: 'error', message: tNotify('saveFailed') });
                },
            }
        );

        setOpenConfig(false);
    };

    const infoFields = [
        { label: t('shipmentNumber'), ref: shipmentNumberInput },
        { label: t('shipper'), ref: shipperInput },
        { label: t('carrier'), ref: carrierInput },
        { label: t('consignee'), ref: consigneeInput },
        { label: t('startLocation'), ref: startLocationInput },
        { label: t('endLocation'), ref: endLocationInput },
        { label: t('note'), ref: noteInput },
    ];

    const setInputValue = (ref: any, value: any = '') => {
        if (ref?.current != null) {
            ref.current.value = value ?? '';
        }
    };

    const deviceOptions = Array.isArray(selectedRecords) ? selectedRecords : [];

    useEffect(() => {
        if (!selectedRecords?.length) {
            setActiveRecordId(null);
            return;
        }
        setActiveRecordId((prev: any) => {
            if (prev && selectedRecords.some((r: any) => r.id === prev)) return prev;
            return selectedRecords[0].id;
        });
    }, [selectedRecords]);

    const activeRecord = React.useMemo(() => {
        if (!selectedRecords?.length) return null;
        if (activeRecordId) return selectedRecords.find((r: any) => r.id === activeRecordId) || selectedRecords[0];
        return selectedRecords[0];
    }, [selectedRecords, activeRecordId]);

    useEffect(() => {
        if (!openConfig || !activeRecord) return;
        const meta = activeRecord.metaData || {};
        const thresholdData = activeRecord.threshold || {};
        setInputValue(shipmentNumberInput, meta.shipment_number ?? '');
        setInputValue(shipperInput, meta.shipper_name ?? '');
        setInputValue(carrierInput, meta.carrier_name ?? '');
        setInputValue(consigneeInput, meta.consignee_name ?? '');
        setInputValue(startLocationInput, meta.start_location ?? '');
        setInputValue(endLocationInput, meta.end_location ?? '');
        setInputValue(noteInput, meta.note ?? '');
        const emails = activeRecord.notifyEmails || [];
        [emailInput1, emailInput2, emailInput3].forEach((ref: any, idx: any) => setInputValue(ref, emails[idx] ?? ''));
        setInputValue(upLimitInput, convertTemperature(thresholdData.upper_limit, upperUnit) ?? '');
        setInputValue(lowLimitInput, convertTemperature(thresholdData.lower_limit, lowerUnit) ?? '');
        const duration = thresholdData.time_limit
            ? secondToTime(thresholdData.time_limit)
            : { value: '', unit: 'minute' };
        setInputValue(timeLimitInput, duration.value ?? '');
        setTimeUnit(duration.unit ?? 'minute');
        setRangeType(thresholdData.type ?? 'continuous');
        const logInterval = activeRecord.interval ? secondToTime(activeRecord.interval) : { value: 10, unit: 'minute' };
        setLoggingInterval(logInterval.value ?? 10);
        setIntervalUnit(logInterval.unit ?? 'minute');
    }, [activeRecord, openConfig, lowerUnit, upperUnit]);

    const renderDeviceDropdown = () => (
        // mr-12 keeps the dropdown clear of the absolutely-positioned × close
        // button in the modal's top-right corner, which it otherwise covers.
        <div className='relative mr-12'>
            <button
                type='button'
                className='flex w-full items-center justify-between rounded-[24px] border border-[#DFE7F3] bg-white px-4 py-3 text-sm font-semibold text-[#1F2A37] shadow-[0px_8px_24px_rgba(31,42,55,0.08)] cursor-pointer'
                onClick={() => setDeviceListOpen((prev: any) => !prev)}
            >
                <span>{activeRecord?.deviceId ?? t('deviceId')}</span>
                {deviceListOpen ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
            </button>
            {deviceListOpen && (
                <div className='absolute left-0 right-0 z-10 mt-2 max-h-40 overflow-y-auto rounded-[20px] border border-[#E5E9F2] bg-white shadow-[0px_12px_28px_rgba(31,42,55,0.16)]'>
                    {deviceOptions.map((record: any) => (
                        <button
                            key={record.id}
                            type='button'
                            aria-pressed={record.id === activeRecord?.id}
                            className={`block w-full px-4 py-2 text-left text-sm text-[#1F2A37] hover:bg-[#F1F5FB] cursor-pointer ${record.id === activeRecord?.id ? 'bg-[#F1F5FB]' : ''}`}
                            onClick={() => {
                                setActiveRecordId(record.id);
                                setDeviceListOpen(false);
                            }}
                        >
                            {record.deviceId}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const renderInformationForm = () => (
        <div className='flex flex-col gap-4'>
            {infoFields.map((field: any) => (
                <label key={field.label} className='flex flex-col gap-1 text-xs font-semibold text-[#4D5A6B]'>
                    <span>{field.label}</span>
                    <input ref={field.ref} className={textInputClasses} />
                </label>
            ))}
        </div>
    );

    const renderConfigureForm = () => (
        <div className='flex flex-col gap-5'>
            <div>
                <p className='text-xs font-semibold uppercase tracking-[0.3em] text-[#7B87A1]'>{t('failAlarm')}</p>
                <div className='mt-3 flex flex-col gap-4 rounded-[24px] bg-white px-4 py-5 shadow-[0px_12px_24px_rgba(31,42,55,0.08)]'>
                    <div>
                        <p className='text-xs font-semibold uppercase tracking-[0.3em] text-[#7B87A1]'>
                            {t('excursionType')}
                        </p>
                        <div className='mt-2 relative grid grid-cols-2 items-center rounded-full bg-[#ECF2FB] p-1 overflow-hidden'>
                            <span
                                className='pointer-events-none absolute top-1 bottom-1 left-1 rounded-full bg-[#3F8EE8] shadow-[0px_10px_20px_rgba(63,142,232,0.35)] transition-transform duration-200'
                                style={{
                                    width: 'calc(50% - 4px)',
                                    transform: rangeType === 'cumulative' ? 'translateX(100%)' : 'translateX(0%)',
                                    zIndex: 0,
                                }}
                            />
                            <button
                                id='continuous'
                                type='button'
                                className={`${pillButton(rangeType === 'continuous')} relative z-10 cursor-pointer`}
                                onClick={() => setRangeType('continuous')}
                            >
                                {t('singleEvent')}
                            </button>
                            <button
                                id='cumulative'
                                type='button'
                                className={`${pillButton(rangeType === 'cumulative')} relative z-10 cursor-pointer`}
                                onClick={() => setRangeType('cumulative')}
                            >
                                {t('cumulative')}
                            </button>
                        </div>
                    </div>
                    <label className='flex flex-col gap-2 text-xs font-semibold text-[#4D5A6B]'>
                        {t('upperLimit')}
                        <div className='flex flex-wrap items-center gap-3'>
                            <input
                                ref={upLimitInput}
                                placeholder={convertTemperature(8, upperUnit)}
                                className={`${textInputClasses} min-w-0 flex-1`}
                            />
                            <button
                                type='button'
                                className={chipButton(upperUnit === 'celsius')}
                                onClick={() => setUpperUnit('celsius')}
                            >
                                °C
                            </button>
                            <button
                                type='button'
                                className={chipButton(upperUnit === 'fahrenheit')}
                                onClick={() => setUpperUnit('fahrenheit')}
                            >
                                °F
                            </button>
                        </div>
                    </label>
                    <label className='flex flex-col gap-2 text-xs font-semibold text-[#4D5A6B]'>
                        {t('lowerLimit')}
                        <div className='flex flex-wrap items-center gap-3'>
                            <input
                                ref={lowLimitInput}
                                placeholder={convertTemperature(2, lowerUnit)}
                                className={`${textInputClasses} min-w-0 flex-1`}
                            />
                            <button
                                type='button'
                                className={chipButton(lowerUnit === 'celsius')}
                                onClick={() => setLowerUnit('celsius')}
                            >
                                °C
                            </button>
                            <button
                                type='button'
                                className={chipButton(lowerUnit === 'fahrenheit')}
                                onClick={() => setLowerUnit('fahrenheit')}
                            >
                                °F
                            </button>
                        </div>
                    </label>
                    <label className='flex flex-col gap-2 text-xs font-semibold text-[#4D5A6B]'>
                        {t('duration')}
                        <div className='flex flex-wrap items-center gap-3'>
                            <input
                                ref={timeLimitInput}
                                placeholder='10'
                                className={`${textInputClasses} min-w-0 flex-1`}
                            />
                            <button
                                type='button'
                                className={chipButton(timeUnit === 'minute')}
                                onClick={() => setTimeUnit('minute')}
                            >
                                {t('M')}
                            </button>
                            <button
                                type='button'
                                className={chipButton(timeUnit === 'hour')}
                                onClick={() => setTimeUnit('hour')}
                            >
                                {t('H')}
                            </button>
                            <button
                                type='button'
                                className={chipButton(timeUnit === 'day')}
                                onClick={() => setTimeUnit('day')}
                            >
                                {t('D')}
                            </button>
                        </div>
                    </label>
                    <label className='flex flex-col gap-2 text-xs font-semibold text-[#4D5A6B]'>
                        <span className='flex items-center gap-2'>
                            {t('workingTime')}
                            <LightTooltip title={t('workingTimeDiscription')} placement='top'>
                                <span className='cursor-help'>
                                    <Help className='h-4 w-4 text-[#9AA4B5]' />
                                </span>
                            </LightTooltip>
                        </span>
                        <WorkingTimeRangesEditor
                            resetKey={`${openConfig}-${selectedDeviceId.join(',')}-${selectedIndex.join(',')}`}
                            initialRanges={alertRanges}
                            offsetString={offsetString}
                            onRangesChange={handleWorkingTimeChange}
                        />
                    </label>
                </div>
            </div>

            <div>
                <p className='text-xs font-semibold uppercase tracking-[0.3em] text-[#7B87A1]'>{t('dataLogging')}</p>
                <div className='mt-3 flex flex-col gap-3 rounded-[24px] bg-white px-4 py-4 shadow-[0px_12px_24px_rgba(31,42,55,0.08)]'>
                    <label className='flex flex-col gap-2 text-xs font-semibold text-[#4D5A6B]'>
                        {t('loggingInterval')}
                        <div className='flex flex-wrap items-center gap-3'>
                            <input
                                ref={intervalInput}
                                disabled
                                value={loggingInterval}
                                readOnly
                                className={`${textInputClasses} min-w-0 flex-1`}
                            />
                            <button type='button' disabled className={chipButton(intervalUnit === 'minute')}>
                                {t('M')}
                            </button>
                            <button type='button' disabled className={chipButton(intervalUnit === 'hour')}>
                                {t('H')}
                            </button>
                            <button type='button' disabled className={chipButton(intervalUnit === 'day')}>
                                {t('D')}
                            </button>
                        </div>
                    </label>
                </div>
            </div>

            <div>
                <p className='text-xs font-semibold uppercase tracking-[0.3em] text-[#7B87A1]'>
                    {t('alarmNotification')}
                </p>
                <div className='mt-3 flex flex-col gap-3 rounded-[24px] bg-white px-4 py-4 shadow-[0px_12px_24px_rgba(31,42,55,0.08)]'>
                    {[emailInput1, emailInput2, emailInput3].map((ref: any, idx: any) => (
                        <label key={idx} className='flex flex-col gap-1 text-xs font-semibold text-[#4D5A6B]'>
                            <span>{t('email')}</span>
                            <input ref={ref} placeholder='name@company.com' className={textInputClasses} />
                        </label>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            open={openConfig}
            onClose={() => setOpenConfig(false)}
            aria-labelledby='modal-modal-title'
            aria-describedby='modal-modal-description'
            className='overflow-scroll'
        >
            <Box sx={modalStyle}>
                <div className='relative max-h-[80vh] overflow-y-auto rounded-[32px] bg-[#F6F8FD] p-5 font-["Inter",sans-serif] text-[#1F2A37] shadow-[0px_25px_60px_rgba(25,33,49,0.35)]'>
                    <button
                        type='button'
                        className='absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-[#E1E5ED] bg-white text-lg font-semibold text-[#94A3B8] cursor-pointer'
                        onClick={() => setOpenConfig(false)}
                    >
                        &times;
                    </button>
                    <div className='space-y-5'>
                        {renderDeviceDropdown()}
                        <div className='relative grid grid-cols-2 items-center rounded-full bg-[#E8EEF8] p-1 text-sm font-semibold overflow-hidden z-[5]'>
                            <span
                                className='pointer-events-none absolute top-1 bottom-1 left-1 rounded-full bg-[#3F8EE8] shadow-[0px_10px_20px_rgba(63,142,232,0.35)] transition-transform duration-200'
                                style={{
                                    width: 'calc(50% - 4px)',
                                    transform: configType === 'alarm' ? 'translateX(100%)' : 'translateX(0%)',
                                    zIndex: 0,
                                }}
                            />
                            <button
                                id='info'
                                type='button'
                                className={`${pillButton(configType === 'info')} relative z-10 cursor-pointer`}
                                onClick={handleSwitch}
                            >
                                {t('information')}
                            </button>
                            <button
                                id='alarm'
                                type='button'
                                disabled={ro}
                                className={`${pillButton(configType === 'alarm')} relative z-10 flex items-center justify-center gap-1 ${ro ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                onClick={handleSwitch}
                            >
                                {ro && <LockOutlinedIcon style={{ fontSize: '13px' }} />}
                                {t('configure')}
                            </button>
                        </div>
                        <div>
                            <div id='infoForm' hidden={configType !== 'info'}>
                                {renderInformationForm()}
                            </div>
                            <div id='alarmForm' hidden={configType !== 'alarm'}>
                                {renderConfigureForm()}
                            </div>
                        </div>
                        <div className='flex gap-3 pt-2'>
                            <button
                                type='button'
                                className='flex-1 rounded-full border border-[#E1E5ED] bg-white py-3 text-sm font-semibold text-[#1F2A37] shadow-[0px_8px_20px_rgba(31,42,55,0.08)] cursor-pointer'
                                onClick={() => setOpenConfig(false)}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type='button'
                                className='flex-1 rounded-full bg-[#3F8EE8] py-3 text-sm font-semibold text-white shadow-[0px_10px_25px_rgba(63,142,232,0.45)] cursor-pointer'
                                onClick={handleSaveAll}
                            >
                                {t('saveAll')}
                            </button>
                        </div>
                    </div>
                </div>
            </Box>
        </Modal>
    );
}

export default Edit;
